<acto-credentials-form>
  <div class="flex flex-col gap-4">
    <div>
      <h3 id="acto-form-heading" class="text-lg font-bold">
        {props.isEditMode ? locale?.t('admin_acto_title_update') : locale?.t('admin_acto_title_config')}
      </h3>
      <p class="text-sm text-gray-500 font-semibold mt-1">
        {props.isEditMode ? locale?.t('admin_acto_subtitle_update') : locale?.t('admin_acto_subtitle_config')}
      </p>
    </div>

    <!-- What has to be true on the Acto side before any of this works. Leads for the same
         reason OnPoint's super-admin requirement and Moodle's web-services note do: it is
         the most common cause of a first attempt failing, and both halves of it are fixed
         by someone other than the person filling this form in. -->
    <div class="alert alert-info text-sm">
      <i class="icon-info" aria-hidden="true"></i>
      <span>{locale?.t('admin_acto_prereq')}</span>
    </div>

    <div class="form-control">
      <label class="label" for="acto-hostname">
        <span class="label-text font-semibold">
          {locale?.t('admin_acto_hostname')} <span class="text-error" aria-hidden="true">*</span>
        </span>
      </label>
      <input
        id="acto-hostname"
        type="url"
        class="input input-bordered w-full"
        placeholder="https://customer.acto.com"
        value="{state.hostname}"
        oninput="{(e) => onField('hostname', e)}"
        required
      />
      <p class="text-xs text-gray-500 mt-1">{locale?.t('admin_acto_hostname_help')}</p>
    </div>

    <div class="form-control">
      <label class="label" for="acto-token">
        <span class="label-text font-semibold">
          {locale?.t('admin_acto_token')} <span class="text-error" aria-hidden="true">*</span>
        </span>
      </label>
      <!-- In edit mode the box carries a masked PLACEHOLDER, not a value: a stored token
           cannot be read back, and putting bullets in `value` would send them to the server
           as the credential the moment someone saved without retyping. Empty means "keep
           what is stored"; typing replaces it. -->
      <input
        id="acto-token"
        type="password"
        class="input input-bordered w-full"
        autocomplete="new-password"
        placeholder="{props.isEditMode ? state.secretMask : ''}"
        value="{state.token}"
        oninput="{(e) => onField('token', e)}"
        required="{!props.isEditMode}"
      />
      <p class="text-xs text-gray-500 mt-1">{locale?.t('admin_acto_token_help')}</p>
      <p if="{props.isEditMode && !state.token}" class="text-xs text-gray-600 mt-1 flex items-center gap-1">
        <i class="icon-metalark-lock" aria-hidden="true"></i>
        {locale?.t('admin_lms_secret_stored_help')}
      </p>
      <p if="{props.isEditMode && state.token}" class="text-xs text-gray-500 mt-1">
        {locale?.t('admin_acto_token_replace_help')}
      </p>
    </div>

    <!-- How the credential is handled, answering the question the token box just raised. -->
    <div class="rounded border border-base-300 bg-base-200 p-3">
      <p class="text-sm font-semibold flex items-center gap-2">
        <i class="icon-metalark-lock" aria-hidden="true"></i>
        {locale?.t('admin_onpoint_credentials_encrypted')}
      </p>
      <p class="text-xs text-gray-600 mt-1">{locale?.t('admin_acto_credentials_notice')}</p>
    </div>

    <!-- What the token can actually reach, populated by a successful test.
         "It authenticated" is not the useful answer: a token can be valid and still see no
         folders, in which case no migration target can be created and it is better to learn
         that here than from an empty picker later. -->
    <div if="{state.probe}"
         class="rounded border p-3 {state.probe.folderCount > 0 ? 'border-success bg-success/10' : 'border-warning bg-warning/10'}">
      <p class="text-sm font-semibold flex items-center gap-2">
        <i class="{state.probe.folderCount > 0 ? 'icon-check_circle' : 'icon-warning'}" aria-hidden="true"></i>
        {state.probe.folderCount > 0
          ? locale?.t('admin_acto_reachable', { count: state.probe.folderCount })
          : locale?.t('admin_acto_no_folders')}
      </p>
      <p class="text-xs text-gray-700 mt-1">
        {state.probe.folderCount > 0
          ? locale?.t('admin_acto_folders_note')
          : locale?.t('admin_acto_no_folders_detail')}
      </p>

      <ul if="{state.probe.folders?.length > 0}" class="mt-2 flex flex-col gap-1">
        <li each="{f in state.probe.folders}" class="text-sm text-gray-700">
          {f.name} <span class="text-gray-500">({f.folderId})</span>
        </li>
      </ul>
    </div>

    <div if="{state.successMessage}" class="alert alert-success text-sm" role="status">
      <i class="icon-check" aria-hidden="true"></i>
      <span>{state.successMessage}</span>
    </div>

    <div if="{state.errorMessage}" class="alert alert-error text-sm" role="alert">
      <i class="icon-error" aria-hidden="true"></i>
      <span>{state.errorMessage}</span>
    </div>
  </div>

  <div class="modal-action">
    <button
      type="button"
      class="btn btn-ghost"
      onclick="{handleCancel}"
      disabled="{state.busy}"
      aria-label="{locale?.t('cancel')}"
    >
      {locale?.t('cancel')}
    </button>
    <button
      type="button"
      class="btn btn-outline"
      onclick="{handleTest}"
      disabled="{!isValid() || state.busy}"
      aria-label="{locale?.t('admin_onpoint_testconnection')}"
    >
      <span if="{state.busy === 'test'}" class="loading loading-spinner loading-sm"></span>
      {state.busy === 'test' ? locale?.t('testing') : locale?.t('admin_onpoint_testconnection')}
    </button>
    <button
      type="button"
      class="btn btn-primary"
      onclick="{handleSave}"
      disabled="{!isValid() || state.busy}"
      aria-label="{locale?.t('admin_onpoint_save')}"
    >
      <span if="{state.busy === 'save'}" class="loading loading-spinner loading-sm"></span>
      {state.busy === 'save' ? locale?.t('saving') : locale?.t('admin_onpoint_save')}
    </button>
  </div>

  <script>
    import { getRepositoryService } from '../../../services/service-loader';
    import { isCredentialSafeUrl } from '../../../utils/localMachineHost';

    /**
     * What an already-stored secret looks like in the box. A placeholder only — see the
     * token input. Eight bullets rather than the real length, which is not ours to leak.
     */
    const SECRET_MASK = '••••••••';

    export default {
      state: {
        hostname: '',
        token: '',
        // On state, not read from the module const directly: Riot resolves template
        // expressions against the instance, so a module-scoped value renders as empty.
        secretMask: SECRET_MASK,
        /**
         * The last successful probe: how many folders the token reached, and which.
         * Null until a test or save succeeds, and cleared on any edit so a stale panel can
         * never describe values that were since changed.
         */
        probe: null,
        successMessage: '',
        errorMessage: '',
        // 'test' | 'save' | null — which action is in flight, so each button shows its own
        // spinner rather than both.
        busy: null
      },

      onMounted() {
        // Re-credentialing an existing connection prefills the domain it was created with;
        // the token is never returned by the API, so it is only ever retyped or kept.
        this.update({
          hostname: this.props.hostname || '',
          token: '',
          probe: null,
          successMessage: '',
          errorMessage: '',
          busy: null
        });
      },

      isValid() {
        const { hostname, token } = this.state;
        if (!hostname?.trim()) return false;
        // A blank token is valid ONLY when editing, where it means "keep the stored one".
        // On a create there is nothing to keep.
        if (!token?.trim() && !this.props.isEditMode) return false;
        // The token is a bearer credential about to be sent to this host, so plaintext is
        // refused up front rather than at save time. Loopback and Docker's host alias are
        // exempt, matching what `ActoProvider.validateLocation` accepts.
        return isCredentialSafeUrl(hostname);
      },

      onField(key, e) {
        // Editing invalidates any prior probe, so a stale panel cannot imply the current
        // values were the ones verified.
        this.update({
          [key]: e.target.value,
          successMessage: '',
          errorMessage: '',
          probe: null
        });
      },

      payload() {
        const body = { hostname: this.state.hostname.trim() };
        // Omitted rather than sent empty: the server reads an absent token on an update as
        // "keep the stored one", and an empty string would be a credential of length zero.
        const token = this.state.token.trim();
        if (token) body.token = token;
        return body;
      },

      async handleTest() {
        if (!this.isValid()) return;
        this.update({ busy: 'test', successMessage: '', errorMessage: '', probe: null });
        try {
          const service = await getRepositoryService();
          const body = this.payload();
          // With the box left blank there is no credential to test but the stored one, and
          // the folders endpoint is precisely "probe this connection with what is stored" —
          // same response shape, so nothing downstream changes.
          const result = body.token
            ? await service.testActoConnection(body)
            : await service.getActoFolders(this.props.repositoryId);
          this.update({
            busy: null,
            probe: result,
            successMessage: this.locale?.t('admin_acto_test_success') || 'Connected'
          });
        } catch (error) {
          this.update({ busy: null, errorMessage: this.messageFor(error) });
        }
      },

      async handleSave() {
        if (!this.isValid()) return;
        this.update({ busy: 'save', successMessage: '', errorMessage: '' });
        try {
          const service = await getRepositoryService();
          const body = this.payload();
          if (this.props.repositoryId) body.repositoryId = this.props.repositoryId;
          const result = await service.connectActo(body);
          this.update({ busy: null, probe: result });
          if (this.props.onsaved) this.props.onsaved(result?.repository);
        } catch (error) {
          this.update({ busy: null, errorMessage: this.messageFor(error) });
        }
      },

      /**
       * The API's own message is the useful one here — it distinguishes a rejected token
       * from a token that may not read content from a domain whose public API was never
       * enabled, and those three are fixed by different people. A generic fallback would
       * discard exactly the distinction the test step exists to make.
       */
      messageFor(error) {
        return error?.response?.data?.error?.message
          || error?.response?.data?.message
          || error?.message
          || this.locale?.t('admin_acto_test_failed')
          || 'Connection failed';
      },

      handleCancel() {
        if (this.props.oncancel) this.props.oncancel();
      }
    }
  </script>
</acto-credentials-form>
